import type { Experiment } from 'types';
import { useState } from 'react';
import { toast } from 'sonner';
import { useExperiment } from '@/state/experiments/useExperiment';
import { parseMechanisms, serializeMechanisms, sameMechanisms, type ExpectedMechanisms } from './expected-mechanisms';

/**
 * The hypothesis card's state and saves.
 *
 * The four pieces of state were previously held in the component. Beyond the convention,
 * two defects came out of that.
 *
 * Every save was fired without a catch, so a failed request left the checkbox ticked and
 * the server unchanged: the UI reported success it had no evidence for. Each save here
 * reverts the local value and says so.
 *
 * The local copy was also seeded from props once and never resynced, so it outranked the
 * server for the life of the mount. Combined with the swallowed failures, a failed save
 * left the card permanently disagreeing with the database until a remount. The draft is
 * now reset whenever the stored value actually changes, compared by content so an
 * ordinary refetch does not discard what someone is in the middle of typing.
 *
 * Save semantics are deliberately unchanged: the hypothesis text needs an explicit Save,
 * mechanism checkboxes save on click, and the free text saves on blur. Three models in
 * one card is a product decision, not something to quietly unify here.
 */
export const useHypothesis = (experiment: Experiment) => {
  const { updateExperiment } = useExperiment(experiment.id);

  const [isEditing, setIsEditing] = useState(false);
  const stored = parseMechanisms(experiment.expectedMechanisms);

  const [draft, setDraft] = useState<ExpectedMechanisms>(stored);
  const [lastStored, setLastStored] = useState<ExpectedMechanisms>(stored);
  const [showCustom, setShowCustom] = useState(stored.custom.length > 0);

  // Adjusting state when a prop changes, rather than in an effect: this runs during
  // render, so nothing is displayed from the outdated draft first.
  if (!sameMechanisms(stored, lastStored)) {
    setLastStored(stored);
    setDraft(stored);
    setShowCustom(stored.custom.length > 0);
  }

  /**
   * Applies the change locally, then persists. On failure the local value goes back to
   * what the server last confirmed, so the card never shows a state that was not saved.
   */
  const save = async (next: ExpectedMechanisms) => {
    setDraft(next);

    try {
      await updateExperiment(experiment.id, { expectedMechanisms: serializeMechanisms(next) });
    } catch (error) {
      console.error('Failed to save expected mechanisms', error);
      setDraft(lastStored);
      setShowCustom(lastStored.custom.length > 0);
      toast.error('Could not save the expected mechanisms');
    }
  };

  const toggleMechanism = (mechanismId: string, checked: boolean) =>
    save({
      ...draft,
      selected: checked ? [...draft.selected, mechanismId] : draft.selected.filter((id) => id !== mechanismId),
    });

  /** Unticking Other clears the text, which is a change worth persisting immediately. */
  const toggleCustom = async (checked: boolean) => {
    setShowCustom(checked);
    if (!checked) await save({ ...draft, custom: '' });
  };

  const setCustom = (custom: string) => setDraft((current) => ({ ...current, custom }));

  const commitCustom = () => save(draft);

  const saveHypothesis = async (hypothesis: string) => {
    try {
      await updateExperiment(experiment.id, { hypothesis });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save hypothesis', error);
      toast.error('Could not save the hypothesis');
    }
  };

  return {
    isEditing,
    startEditing: () => setIsEditing(true),
    stopEditing: () => setIsEditing(false),
    saveHypothesis,
    selected: draft.selected,
    custom: draft.custom,
    // From the stored value, not the draft: the author badge should not appear while
    // someone is still typing a custom mechanism, or vanish when a save fails.
    hasStoredMechanisms: stored.selected.length > 0 || stored.custom.trim().length > 0,
    showCustom,
    toggleMechanism,
    toggleCustom,
    setCustom,
    commitCustom,
  };
};
